/**
 * Kontroll's questions to an employee about one registration (#275,
 * backend/controllers/admin/clarificationController.js). An open question holds the record's approval on
 * the server; a correction of its times closes it there. `delivery` is what the message channel did:
 * push, email (the fixed notice), none (the in-app list only) or failed.
 *
 * The employee's own «Forklar» from the phone (#303 PROM-39, POST /shifts/:id/explanation) lands here too: a
 * row with no question, the employee's text as `reply` recorded by the employee, status answered, and the
 * times they suggest (never applied). Approval closes it as approved, a correction as corrected.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from './client';

export type ClarificationStatus = 'open' | 'answered' | 'withdrawn' | 'corrected' | 'approved';
export type Delivery = 'pending' | 'push' | 'email' | 'none' | 'failed';
export interface Clarification {
  id: number;
  shift_id: number;
  owner_user_id: number;
  asked_by: number | null;
  /** the asking manager's name, for «følges opp av» */
  asked_by_name?: string | null;
  reply_recorded_by_name?: string | null;
  /** null when the employee started it from the phone */
  question: string | null;
  status: ClarificationStatus;
  delivery: Delivery;
  reply: string | null;
  reply_recorded_by: number | null;
  replied_at: string | null;
  closed_by: number | null;
  closed_at: string | null;
  created_at: string;
  /** the times the employee believes are right; shown, never applied */
  suggested_in?: string | null;
  suggested_out?: string | null;
}

/** The employee wrote it: their own explanation, or their answer to the manager's question. */
export const fromEmployee = (c: Clarification) => c.reply != null && c.reply_recorded_by === c.owner_user_id;

export function useClarifications(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['clarifications', from, to],
    enabled,
    queryFn: () => get<Clarification[]>(`/admin/clarifications?from=${from}&to=${to}`),
    staleTime: 30_000,
  });
}

export function useClarificationWrites() {
  const qc = useQueryClient();
  // a question changes what the record may do, so the shifts are read again too
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['clarifications'] }),
      qc.invalidateQueries({ queryKey: ['shifts'] }),
    ]);
  return {
    ask: useMutation({
      mutationFn: ({ shiftId, question }: { shiftId: number; question: string }) =>
        post<Clarification>(`/admin/shifts/${shiftId}/clarification`, { question }),
      onSettled: done,
    }),
    reply: useMutation({
      mutationFn: ({ id, reply }: { id: number; reply: string }) =>
        post<Clarification>(`/admin/clarifications/${id}/reply`, { reply }),
      onSettled: done,
    }),
    withdraw: useMutation({
      mutationFn: ({ id, status }: { id: number; status: ClarificationStatus }) =>
        post<Clarification>(`/admin/clarifications/${id}/withdraw`, { status }),
      onSettled: done,
    }),
    resend: useMutation({
      mutationFn: (id: number) => post<Clarification>(`/admin/clarifications/${id}/resend`, {}),
      onSettled: done,
    }),
  };
}
